import React from "react";
import {
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Stack,
} from "@mui/material";
import { JournalFiltersProps } from "../types";
import { BranchSelector } from "../../../components/BranchSelector";
import { useBranch } from "../../../components/BranchContext";

export const JournalFilters: React.FC<JournalFiltersProps> = ({
  filters,
  onFiltersChange,
  onResetFilters,
  onApplyFilters,
}) => {
  const { selectedBranch } = useBranch();

  const sourceModules = [
    { value: "", label: "All Sources" },
    { value: "POS", label: "POS" },
    { value: "Payroll", label: "Payroll" },
    { value: "Procurement", label: "Procurement" },
    { value: "Manual", label: "Manual" },
    { value: "Adjustment", label: "Adjustment" },
  ];

  const statusOptions = [
    { value: "", label: "All Statuses" },
    { value: "Draft", label: "Draft" },
    { value: "Posted", label: "Posted" },
    { value: "Reversed", label: "Reversed" },
    { value: "Pending", label: "Pending" },
  ];

  const users = [
    { value: "", label: "All Users" },
    { value: "john.doe", label: "John Doe" },
    { value: "jane.smith", label: "Jane Smith" },
    { value: "admin", label: "Administrator" },
    { value: "accountant", label: "Accountant" },
  ];

  const handleFilterChange = (field: string, value: string) => {
    onFiltersChange({ [field]: value });
  };

  // const getActiveFiltersCount = () => {
  //   return Object.values(filters).filter(
  //     (value) => value && value.toString().trim() !== ""
  //   ).length;
  // };

  return (
    <Stack spacing={3}>
      {/* Branch Override */}
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
          Branch Override
        </Typography>
        <Typography
          variant="caption"
          sx={{ color: "text.secondary", mb: 2, display: "block" }}
        >
          Override global branch selection for this view only
        </Typography>
        <BranchSelector
          selectedBranch={
            filters.selectedBranch || selectedBranch || "organization"
          }
          onBranchChange={(branchId) =>
            handleFilterChange(
              "selectedBranch",
              branchId === "organization" ? "" : branchId
            )
          }
          size="small"
          showLabel={false}
        />
      </Box>

      {/* Date Range */}
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
          Date Range
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="From"
              type="date"
              value={filters.dateFrom}
              onChange={(e) => handleFilterChange("dateFrom", e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="To"
              type="date"
              value={filters.dateTo}
              onChange={(e) => handleFilterChange("dateTo", e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
          </Grid>
        </Grid>
      </Box>

      {/* Source & Status */}
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
          Source & Status
        </Typography>
        <Stack spacing={2}>
          <FormControl fullWidth size="small">
            <InputLabel>Source Module</InputLabel>
            <Select
              value={filters.sourceModule}
              label="Source Module"
              onChange={(e) =>
                handleFilterChange("sourceModule", e.target.value)
              }
            >
              {sourceModules.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small">
            <InputLabel>Status</InputLabel>
            <Select
              value={filters.status}
              label="Status"
              onChange={(e) => handleFilterChange("status", e.target.value)}
            >
              {statusOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small">
            <InputLabel>Posted By</InputLabel>
            <Select
              value={filters.postedBy}
              label="Posted By"
              onChange={(e) => handleFilterChange("postedBy", e.target.value)}
            >
              {users.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </Box>

      {/* Search Fields */}
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
          Search
        </Typography>
        <Stack spacing={2}>
          <TextField
            fullWidth
            label="Journal ID"
            value={filters.journalEntryId}
            onChange={(e) =>
              handleFilterChange("journalEntryId", e.target.value)
            }
            placeholder="JE-2024-0001"
            size="small"
          />

          <TextField
            fullWidth
            label="Reference ID"
            value={filters.referenceId}
            onChange={(e) => handleFilterChange("referenceId", e.target.value)}
            placeholder="Order, Invoice, etc."
            size="small"
          />

          <TextField
            fullWidth
            label="Narration Contains"
            value={filters.narrationContains}
            onChange={(e) =>
              handleFilterChange("narrationContains", e.target.value)
            }
            placeholder="Search in description"
            size="small"
          />
        </Stack>
      </Box>
    </Stack>
  );
};
